/**
 * Storage Service
 * High-level API matching Amplify Storage interface
 * Handles tenant-aware S3 operations
 */

import { uploadFile, getSignedDownloadUrl, deleteFile, getPublicUrl } from './s3Client';
import { getTenantConfig, getTenantS3Key } from '../../config/tenantConfig';

/**
 * Upload file to S3 with tenant prefix
 * @param {string} key - Object key (tenant prefix will be added automatically)
 * @param {Blob|Buffer} data - File data
 * @param {Object} options - Upload options
 * @param {string} options.contentType - MIME type
 * @param {string} options.level - Access level: 'public' or 'private' (default: 'private')
 * @param {Object} options.metadata - Custom metadata
 * @returns {Promise<string>} Full S3 key with tenant prefix
 */
export const put = async (key, data, options = {}) => {
  const config = getTenantConfig();

  // Add tenant prefix to key
  const fullKey = getTenantS3Key(key);

  // Determine ACL based on level
  const acl = options.level === 'public' ? 'public-read' : 'private';

  await uploadFile(config.s3.bucket, fullKey, data, {
    contentType: options.contentType,
    metadata: options.metadata,
    acl
  });

  return fullKey;
};

/**
 * Get file from S3 (returns signed URL)
 * @param {string} key - Object key
 * @param {Object} options - Get options
 * @param {number} options.expires - URL expiration in seconds (default: 3600)
 * @param {string} options.level - Access level: 'public' or 'private' (default: 'private')
 * @returns {Promise<string>} Signed URL or public URL
 */
export const get = async (key, options = {}) => {
  const config = getTenantConfig();

  // Clean and prefix key
  const cleanKey = key.replace(/^public\//, '');
  const fullKey = getTenantS3Key(cleanKey);

  // If public level, return public URL (no signing needed)
  if (options.level === 'public') {
    return getPublicUrl(config.s3.bucket, fullKey, config.s3.region);
  }

  // Generate signed URL for private files
  const expiresIn = options.expires || 3600;
  return await getSignedDownloadUrl(config.s3.bucket, fullKey, expiresIn);
};

/**
 * Delete file from S3
 * @param {string} key - Object key
 * @param {Object} options - Delete options
 * @param {string} options.level - Access level (not used, kept for compatibility)
 * @returns {Promise<void>}
 */
export const remove = async (key, options = {}) => {
  const config = getTenantConfig();

  // Clean and prefix key
  const cleanKey = key.replace(/^public\//, '');
  const fullKey = getTenantS3Key(cleanKey);

  await deleteFile(config.s3.bucket, fullKey);
};
